import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { ADMIN_I18N } from "@/i18n/admin";
import RequestChat from "@/components/RequestChat";
import ContentManager from "@/pages/AdminContent";
import {
  Activity, Users, MailOpen, DollarSign, Clock, CheckCircle2, Eye,
  AlertCircle, LayoutDashboard, MessageSquare, Send, RefreshCw, FileEdit
} from "lucide-react";

const STATUS_COLOR = {
  pending: "#8a8a8a",
  approved: "#00d4ff",
  in_progress: "#00d4ff",
  rejected: "#ff006e",
  completed: "#0a0a0a",
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const { lang } = useLang();
  const T = ADMIN_I18N[lang] || ADMIN_I18N.en;
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");

  const loadAll = useCallback(async () => {
    try {
      const [s, r, c, ct] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/requests"),
        api.get("/admin/clients"),
        api.get("/admin/contacts"),
      ]);
      setStats(s.data);
      setRequests(r.data);
      setClients(c.data);
      setContacts(ct.data);
      if (r.data.length && !selectedId) setSelectedId(r.data[0].request_id);
    } catch {
      /* ignore */
    }
  }, [selectedId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const selected = requests.find((r) => r.request_id === selectedId);
  const filteredRequests = filterStatus === "all"
    ? requests
    : requests.filter((r) => r.status === filterStatus);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pt-24 pb-20">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        {/* Header bar */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00d4ff] mb-2 flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 bg-[#ff006e] pulse-dot" />
              · {T.console}
            </div>
            <h1 className="font-mono font-bold tracking-tighter text-3xl sm:text-4xl">
              {T.title}
            </h1>
            <p className="mt-2 font-sans text-sm text-white/60">
              {T.welcome(user?.name || user?.email || "")}
            </p>
          </div>
          <button
            onClick={loadAll}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 border border-white/30 hover:border-[#00d4ff] font-mono text-xs uppercase tracking-[0.2em] transition-colors self-start lg:self-auto"
          >
            <RefreshCw size={12} />
            {T.refresh}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-px bg-white/20 border border-white/20 w-fit mb-8">
          <AdminTab tid="admin-tab-overview" active={tab === "overview"} onClick={() => setTab("overview")} icon={LayoutDashboard}>
            {T.tabs.overview}
          </AdminTab>
          <AdminTab tid="admin-tab-requests" active={tab === "requests"} onClick={() => setTab("requests")} icon={MessageSquare}>
            {T.tabs.requests} <span className="ml-1 text-[#00d4ff]">{requests.length}</span>
          </AdminTab>
          <AdminTab tid="admin-tab-clients" active={tab === "clients"} onClick={() => setTab("clients")} icon={Users}>
            {T.tabs.clients} <span className="ml-1 text-[#00d4ff]">{clients.length}</span>
          </AdminTab>
          <AdminTab tid="admin-tab-contacts" active={tab === "contacts"} onClick={() => setTab("contacts")} icon={MailOpen}>
            {T.tabs.inbox} <span className="ml-1 text-[#00d4ff]">{contacts.length}</span>
          </AdminTab>
          <AdminTab tid="admin-tab-content" active={tab === "content"} onClick={() => setTab("content")} icon={FileEdit}>
            {T.tabs.content}
          </AdminTab>
        </div>

        {/* Content */}
        {tab === "overview" && <OverviewPanel T={T} stats={stats} requests={requests} clients={clients} onOpenTab={setTab} onOpenRequest={(id) => { setSelectedId(id); setTab("requests"); }} />}

        {tab === "requests" && (
          <RequestsPanel
            T={T}
            requests={filteredRequests}
            selected={selected}
            setSelectedId={setSelectedId}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            onReload={loadAll}
          />
        )}

        {tab === "clients" && <ClientsPanel T={T} clients={clients} onOpenClient={(uid) => {
          const req = requests.find((r) => r.client_user_id === uid);
          if (req) { setSelectedId(req.request_id); setTab("requests"); }
        }} />}

        {tab === "contacts" && <ContactsPanel T={T} contacts={contacts} />}

        {tab === "content" && <ContentManager />}
      </div>
    </div>
  );
}

const AdminTab = ({ tid, active, onClick, icon: Icon, children }) => (
  <button
    data-testid={tid}
    onClick={onClick}
    className={`flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
      active ? "bg-[#00d4ff] text-[#0a0a0a]" : "bg-[#0a0a0a] text-white hover:bg-white/10"
    }`}
  >
    <Icon size={14} />
    {children}
  </button>
);

/* ---------------------------- OVERVIEW ---------------------------- */
function OverviewPanel({ T, stats, requests, clients, onOpenTab, onOpenRequest }) {
  if (!stats) return <div className="font-mono text-xs text-white/60">{T.loading}</div>;
  const kpis = [
    { key: "total_requests", label: T.kpi.total_requests, icon: MessageSquare, color: "#ffffff" },
    { key: "pending", label: T.kpi.pending, icon: Clock, color: "#ffb347" },
    { key: "in_progress", label: T.kpi.in_progress, icon: Activity, color: "#00d4ff" },
    { key: "awaiting_payment", label: T.kpi.awaiting_payment, icon: DollarSign, color: "#ff006e" },
    { key: "completed", label: T.kpi.completed, icon: CheckCircle2, color: "#7fffa1" },
    { key: "total_clients", label: T.kpi.total_clients, icon: Users, color: "#ffd9ec" },
  ];

  const latest = requests.slice(0, 6);
  const topClients = clients
    .slice()
    .sort((a, b) => (b.request_count || 0) - (a.request_count || 0))
    .slice(0, 5);

  return (
    <div className="space-y-8">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/20 border border-white/20">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <motion.div
              key={k.key}
              data-testid={`kpi-${k.key}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-[#0a0a0a] p-5 hover:bg-white/5 transition-colors"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <Icon size={14} style={{ color: k.color }} />
              </div>
              <div className="font-mono font-bold text-3xl lg:text-4xl tracking-tighter" style={{ color: k.color }}>
                {stats[k.key] ?? 0}
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                {k.label}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Recent + Top clients */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-white/20 bg-white/[0.02]">
          <div className="px-5 py-3 border-b border-white/20 flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em]">· {T.recent}</span>
            <button
              onClick={() => onOpenTab("requests")}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00d4ff] hover:text-white"
            >
              {T.view_all}
            </button>
          </div>
          <ul className="divide-y divide-white/10">
            {latest.length === 0 ? (
              <li className="px-5 py-8 font-mono text-xs text-white/40">{T.no_requests}</li>
            ) : (
              latest.map((r) => (
                <li key={r.request_id}>
                  <button
                    onClick={() => onOpenRequest(r.request_id)}
                    className="w-full text-left px-5 py-4 hover:bg-white/5 transition-colors flex items-center gap-4"
                  >
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: STATUS_COLOR[r.status] || "#8a8a8a" }}>
                      {r.status}
                    </span>
                    <span className="flex-1 font-mono text-sm text-white truncate">{r.title}</span>
                    <span className="font-mono text-[10px] text-white/50 truncate hidden sm:block">{r.client_email}</span>
                    <span className="font-mono text-[10px] text-white/40">{new Date(r.created_at).toLocaleDateString()}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>

        <div className="border border-white/20 bg-white/[0.02]">
          <div className="px-5 py-3 border-b border-white/20 font-mono text-[10px] uppercase tracking-[0.25em]">· {T.top_clients}</div>
          <ul className="divide-y divide-white/10">
            {topClients.length === 0 ? (
              <li className="px-5 py-8 font-mono text-xs text-white/40">{T.no_clients}</li>
            ) : (
              topClients.map((c) => (
                <li key={c.user_id} className="px-5 py-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-white truncate">{c.name || c.email}</div>
                    <div className="font-mono text-[10px] text-white/40 truncate">{c.email}</div>
                  </div>
                  <div className="font-mono text-lg font-bold text-[#00d4ff]">{c.request_count}</div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- REQUESTS ---------------------------- */
function RequestsPanel({ T, requests, selected, setSelectedId, filterStatus, setFilterStatus, onReload }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <aside className="lg:col-span-4">
        <div className="border border-white/20 bg-white/[0.02]">
          <div className="px-4 py-3 border-b border-white/20 flex flex-wrap gap-1">
            {["all", "pending", "approved", "in_progress", "completed", "rejected"].map((s) => (
              <button
                key={s}
                data-testid={`admin-filter-${s}`}
                onClick={() => setFilterStatus(s)}
                className={`font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border transition-colors ${
                  filterStatus === s ? "bg-[#00d4ff] text-[#0a0a0a] border-[#00d4ff]" : "border-white/20 text-white hover:border-white"
                }`}
              >
                {T.filters[s] || s}
              </button>
            ))}
          </div>
          <ul className="divide-y divide-white/10 max-h-[70vh] overflow-y-auto">
            {requests.length === 0 ? (
              <li className="px-4 py-8 font-mono text-xs text-white/40">{T.no_matching}</li>
            ) : (
              requests.map((r, i) => (
                <li key={r.request_id}>
                  <button
                    data-testid={`admin-request-item-${i}`}
                    onClick={() => setSelectedId(r.request_id)}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      selected?.request_id === r.request_id ? "bg-white/10" : "hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: STATUS_COLOR[r.status] }}>
                        {r.status}
                      </span>
                      <span className="font-mono text-[10px] text-white/40">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="font-mono font-medium text-sm text-white truncate">{r.title}</div>
                    <div className="mt-0.5 font-mono text-[10px] text-white/50 truncate">{r.client_email}</div>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      </aside>

      <section className="lg:col-span-8">
        {selected ? (
          <AdminRequestDetail T={T} request={selected} onReload={onReload} />
        ) : (
          <div className="border border-white/20 bg-white/[0.02] p-10 font-mono text-xs text-white/40">
            {T.select_request}
          </div>
        )}
      </section>
    </div>
  );
}

function AdminRequestDetail({ T, request, onReload }) {
  const [showAnswers, setShowAnswers] = useState(false);
  const [amount, setAmount] = useState(500);
  const [portion, setPortion] = useState("deposit");
  const [paymentLink, setPaymentLink] = useState(request.payment_link || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const updateStatus = async (status) => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/requests/${request.request_id}`, { status });
      await onReload();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  const issueOrder = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/requests/${request.request_id}/payment-order`, {
        amount: Number(amount), currency: "USD", portion,
        payment_link: paymentLink.trim() || null,
      });
      await onReload();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="border border-white/20 bg-white/[0.02] p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
              {request.request_id}
            </div>
            <h2 className="mt-1 font-mono font-bold tracking-tight text-2xl">{request.title}</h2>
            <div className="mt-2 flex flex-wrap gap-3 font-mono text-[10px] uppercase tracking-[0.2em]">
              <span style={{ color: STATUS_COLOR[request.status] }}>· {request.status}</span>
              <span className="text-white/60">{T.payment.payment_lbl}: {request.payment_status}</span>
              {request.payment_amount ? <span className="text-[#ff006e]">${request.payment_amount} {request.payment_currency}</span> : null}
            </div>
            <div className="mt-2 font-mono text-xs text-white/60">
              {request.client_name || request.client_email} · {request.client_email}
            </div>
          </div>
          <button
            onClick={() => setShowAnswers((v) => !v)}
            data-testid="admin-toggle-brief"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#00d4ff] hover:text-white"
          >
            {showAnswers ? T.hide_brief : T.view_brief}
          </button>
        </div>

        {showAnswers && (
          <div className="mt-6 space-y-3 max-h-[320px] overflow-y-auto pr-2 border-t border-white/20 pt-4">
            {Object.entries(request.answers || {}).map(([k, v]) => (
              <div key={k}>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{k}</div>
                <div className="mt-0.5 font-sans text-sm whitespace-pre-wrap">{v || "—"}</div>
              </div>
            ))}
          </div>
        )}

        {/* Controls */}
        <div className="mt-6 pt-6 border-t border-dashed border-white/20">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">
            · {T.status}
          </div>
          <div className="flex flex-wrap gap-px bg-white/20 border border-white/20 w-fit">
            {["approved", "in_progress", "rejected", "completed"].map((s) => (
              <button
                key={s}
                data-testid={`admin-status-${s}`}
                disabled={busy}
                onClick={() => updateStatus(s)}
                className="bg-[#0a0a0a] hover:bg-[#00d4ff] hover:text-[#0a0a0a] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
              >
                {T.revert_status[s] || s}
              </button>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap items-end gap-3 p-4 bg-white/5 border border-white/10">
            <DollarSign size={16} className="text-[#ff006e] mb-2" />
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">{T.payment.amount}</div>
              <input
                data-testid="admin-payment-amount"
                type="number" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-28 px-3 py-2 border border-white/30 bg-transparent font-sans text-sm text-white"
              />
            </label>
            <label className="block">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">{T.payment.portion}</div>
              <select
                data-testid="admin-payment-portion"
                value={portion} onChange={(e) => setPortion(e.target.value)}
                className="px-3 py-2 border border-white/30 bg-[#0a0a0a] font-sans text-sm text-white"
              >
                <option value="deposit">{T.payment.deposit}</option>
                <option value="balance">{T.payment.balance}</option>
                <option value="full">{T.payment.full}</option>
              </select>
            </label>
            <label className="block flex-1 min-w-[220px]">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">
                {T.payment.link_lbl}
              </div>
              <input
                data-testid="admin-payment-link"
                type="url"
                value={paymentLink}
                onChange={(e) => setPaymentLink(e.target.value)}
                placeholder="https://payoneer.com/... (optional)"
                className="w-full px-3 py-2 border border-white/30 bg-transparent font-sans text-sm text-white"
              />
            </label>
            <button
              onClick={issueOrder}
              disabled={busy || !amount}
              data-testid="admin-issue-order"
              className="px-5 py-2 bg-[#ff006e] text-white font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-white hover:text-[#0a0a0a] transition-colors disabled:opacity-50"
            >
              <span className="inline-flex items-center gap-2">
                <Send size={12} />
                {T.payment.issue}
              </span>
            </button>
          </div>
          {err && <div className="mt-3 font-mono text-xs text-[#ff006e]">{err}</div>}
        </div>
      </div>

      {/* Dark chat wrapper */}
      <div className="admin-chat">
        <RequestChat requestId={request.request_id} />
      </div>
    </div>
  );
}

/* ---------------------------- CLIENTS ---------------------------- */
function ClientsPanel({ T, clients, onOpenClient }) {
  return (
    <div className="border border-white/20 bg-white/[0.02] overflow-hidden">
      <table className="w-full text-left">
        <thead className="border-b border-white/20 bg-white/5">
          <tr>
            {T.clients_cols.map((h) => (
              <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/10">
          {clients.length === 0 ? (
            <tr><td colSpan="6" className="px-4 py-8 font-mono text-xs text-white/40 text-center">{T.no_clients}</td></tr>
          ) : (
            clients.map((c, i) => (
              <tr
                key={c.user_id}
                data-testid={`admin-client-row-${i}`}
                className="hover:bg-white/5 cursor-pointer"
                onClick={() => c.request_count > 0 && onOpenClient(c.user_id)}
              >
                <td className="px-4 py-3 font-mono text-sm">{c.name || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-white/70">{c.email}</td>
                <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{c.auth_provider}</td>
                <td className="px-4 py-3 font-mono text-sm text-[#00d4ff]">{c.request_count}</td>
                <td className="px-4 py-3 font-mono text-xs text-white/60">
                  {c.last_request ? (
                    <span>
                      <span style={{ color: STATUS_COLOR[c.last_request.status] }}>{c.last_request.status}</span> · {c.last_request.title}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-white/50">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------------------- CONTACTS ---------------------------- */
function ContactsPanel({ T, contacts }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {contacts.length === 0 ? (
        <div className="border border-white/20 bg-white/[0.02] p-10 font-mono text-xs text-white/40">
          {T.inbox_empty}
        </div>
      ) : (
        contacts.map((c, i) => (
          <div
            key={c.contact_id || i}
            data-testid={`admin-contact-${i}`}
            className="border border-white/20 bg-white/[0.02] p-5 hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="font-mono text-sm text-white">{c.name}</div>
              <div className="font-mono text-[10px] text-white/40">
                {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
              </div>
            </div>
            <div className="mt-1 font-mono text-xs text-white/70">
              {c.email} · {c.project_type} · {c.budget}
            </div>
            <p className="mt-3 font-sans text-sm text-white/80 whitespace-pre-wrap">{c.message}</p>
            <div className="mt-4 flex gap-2">
              <a
                href={`mailto:${c.email}?subject=Re: your Kuryarti inquiry`}
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/30 hover:border-[#00d4ff] font-mono text-[10px] uppercase tracking-[0.2em]"
              >
                <MailOpen size={11} /> {T.reply}
              </a>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
